/**
 * "电表维度电费通知单"三套模板(津西 jingxi / 通用 common / 海四达 haisida)的配置与数据构建。
 *
 * 三者原本是三个近乎逐字复制的组件(statement_jingxi / statement_JingXiStatementCommon /
 * statement_haisida),仅在"倍率字段、除法口径、是否显示电量列/设备号、备注、汇总行、签署栏"
 * 上有差异。现合并为单组件 MeterStatement.vue + 本配置,差异全部下沉为配置项。
 *
 * ⚠️ 结算涉及金额,buildMeterRows 严格保留了各模板原有的计算口径(包括原代码中
 *    看似不一致的"是否除以倍率"的差异),重构未修改任何业务算法。
 */

/**
 * 通用行结构(11 行):尖/峰/平/谷(充)→ 尖/峰/平/谷(放)→ 充电电费 → 放电电费 → 合计。
 * 第 10 行(index=10,合计)用于累加 countAllData。
 *
 * 每列均为"数据源字段名"映射(而非展示值),各字段含义:
 * - meterNameField : 电表名称取值字段
 * - direction      : 储能方向标签(储能充电 / 储能放电 / 合计),仅用于计费取负判断,不展示
 * - itemLabel      : 明细项名称(展示在"功率"列)
 * - lastReadingField : 上月读数取值字段
 * - currReadingField : 本月读数取值字段
 * - billQtyField   : 计费电量取值字段
 * - priceField     : 市电电价取值字段
 * - feeField       : 电费取值字段(充电为电费/放电为收益)
 * 倍率(rate)字段名及是否带 devId 因模板而异,由 config 注入,不写在此。
 */
export const METER_ROWS = [
  { meterNameField: "meterName", direction: "储能充电", itemLabel: "正向有功尖电量(kWh)", lastReadingField: "lastSharpChg", currReadingField: "thisSharpChg", billQtyField: "qoeSharpChg", priceField: "priceSharpChg", feeField: "costSharpChg" },
  { meterNameField: "meterName", direction: "储能充电", itemLabel: "正向有功峰电量(kWh)", lastReadingField: "lastPeakChg", currReadingField: "thisPeakChg", billQtyField: "qoePeakChg", priceField: "pricePeakChg", feeField: "costPeakChg" },
  { meterNameField: "meterName", direction: "储能充电", itemLabel: "正向有功平电量(kWh)", lastReadingField: "lastFlatChg", currReadingField: "thisFlatChg", billQtyField: "qoeFlatChg", priceField: "priceFlatChg", feeField: "costFlatChg" },
  { meterNameField: "meterName", direction: "储能充电", itemLabel: "正向有功谷电量(kWh)", lastReadingField: "lastValleyChg", currReadingField: "thisValleyChg", billQtyField: "qoeValleyChg", priceField: "priceValleyChg", feeField: "costValleyChg" },
  { meterNameField: "meterName", direction: "储能放电", itemLabel: "反向有功尖电量(kWh)", lastReadingField: "lastSharpDis", currReadingField: "thisSharpDis", billQtyField: "qoeSharpDis", priceField: "priceSharpDis", feeField: "incomeSharpDis" },
  { meterNameField: "meterName", direction: "储能放电", itemLabel: "反向有功峰电量(kWh)", lastReadingField: "lastPeakDis", currReadingField: "thisPeakDis", billQtyField: "qoePeakDis", priceField: "pricePeakDis", feeField: "incomePeakDis" },
  { meterNameField: "meterName", direction: "储能放电", itemLabel: "反向有功平电量(kWh)", lastReadingField: "lastFlatDis", currReadingField: "thisFlatDis", billQtyField: "qoeFlatDis", priceField: "priceFlatDis", feeField: "incomeFlatDis" },
  { meterNameField: "meterName", direction: "储能放电", itemLabel: "反向有功谷电量(kWh)", lastReadingField: "lastValleyDis", currReadingField: "thisValleyDis", billQtyField: "qoeValleyDis", priceField: "priceValleyDis", feeField: "incomeValleyDis" },
  { meterNameField: "meterName", direction: "储能充电", itemLabel: "充电电费", lastReadingField: "lastTotalChg", currReadingField: "thisTotalChg", billQtyField: "qoeTotalChg", priceField: "", feeField: "costTotalChg" },
  { meterNameField: "meterName", direction: "储能放电", itemLabel: "放电电费", lastReadingField: "lastTotalDis", currReadingField: "thisTotalDis", billQtyField: "qoeTotalDis", priceField: "", feeField: "incomeTotalDis" },
  { meterNameField: "合计", direction: "合计", itemLabel: "合计", lastReadingField: "", currReadingField: "", billQtyField: "", priceField: "", feeField: "actuallyIncome" }
];

/** 累加合计行(index=10)的下标 */
export const TOTAL_ROW_INDEX = 10;

const $str = () => window.$str;

/**
 * 各模板差异配置。
 * - magField:倍率字段名(数据源字段)
 * - fixedRate:写死倍率(不为 null 时忽略 magField,如津西写死 240)
 * - divideDischarge / divideActually / divideRatio2:上月读数/本月读数/电费 列数值是否除以倍率(严格对齐原代码)
 * - divideBillQty:计费电量列是否除以倍率(默认 true;common 模板为 false,不除倍率)
 * - useAbsCheck:price/fee 非空判断是否用 Math.abs(海四达用)
 * - roundCountAll:合计累加后是否 toFixed(4)/1(津西、海四达用)
 * - showDevId:电表名是否拼接 (devId)
 * - showOriginElecQuantity:是否显示"电量数据"列(本月读数-上月读数)
 * - showReadingDate:表头"上月/本月读数"是否带日期
 * - summaryType:底部汇总行样式 'default' | 'haisida'
 * - signerType:签署栏样式 'qidian' | 'party'
 * - remarks:() => string[] 备注文案(运行时取 $str,保证国际化)
 */
export const METER_TEMPLATES = {
  jingxi: {
    magField: "magnification",
    fixedRate: 240,
    divideDischarge: true,
    divideActually: true,
    divideRatio2: false,
    useAbsCheck: false,
    roundCountAll: true,
    chargeNegative: false,
    showDevId: true,
    showOriginElecQuantity: false,
    showReadingDate: true,
    summaryType: "default",
    signerType: "",
    remarks: () => [
      $str().getKey("收益管理/备注1：计算公式：月度甲方使用储能电费=储能当月放电电量×峰时电价-储能当月充电电电量×谷平时电价。"),
      $str().getKey("收益管理/备注2：如江门供电局与甲方或丙方电费结算电价调整，甲方或丙方应书面通知乙方，优惠电价也作相应调整。"),
      $str().getKey("收益管理/备注3：表中按合同协议价进行电费计算=月度甲方使用储能电费*84%。"),
      $str().getKey("收益管理/备注4：以上单价含13%增值税。"),
      $str().getKey("收益管理/备注5：表中甲方与江门供电局结算的市电电价：尖峰时段电价:1.44050000元/kWh，峰时段电价:1.15240000元/kWh、平时段电价:0.67790000元/kWh、谷时段电价:0.25750000元/kWh。如江门供电局与甲方或丙方电费结算电价调整，甲方或丙方应书面通知乙方，优惠电价也作相应调整。")
    ]
  },
  common: {
    magField: "meterMagnification",
    fixedRate: null,
    divideDischarge: false,
    divideActually: false,
    divideBillQty: false,
    divideRatio2: true,
    useAbsCheck: false,
    roundCountAll: false,
    chargeNegative: true,
    showDevId: false,
    showOriginElecQuantity: false,
    showReadingDate: false,
    summaryType: "default",
    signerType: "",
    remarks: () => [
      $str().getKey("收益管理/备注1：计算公式：月度甲方使用储能电费=储能当月放电电量×峰时电价-储能当月充电电电量×谷平时电价。"),
      $str().getKey("收益管理/备注2：如供电局与甲方或丙方电费结算电价调整，甲方或丙方应书面通知乙方，优惠电价也作相应调整。"),
      $str().getKey("收益管理/备注3：表中按合同协议价进行电费计算=月度甲方使用储能电费*84%。"),
      $str().getKey("收益管理/备注4：以上单价含13%增值税。")
    ]
  },
  haisida: {
    magField: "meterMagnification",
    fixedRate: null,
    divideDischarge: true,
    divideActually: true,
    divideRatio2: false,
    useAbsCheck: true,
    roundCountAll: true,
    chargeNegative: false,
    showDevId: true,
    showOriginElecQuantity: true,
    showReadingDate: false,
    summaryType: "haisida",
    signerType: "",
    remarks: () => [
      $str().getKey("收益管理/收益计算公式：甲方支付给乙方的节电费用（乙方节电效益）=【（乙方放电度数*放电时段电价）-（乙方谷值充电度数*谷值电价）-（乙方平值充电度数*平值电价）】*乙方分成比例"),
      $str().getKey("收益管理/如对内容有异议请3日内反馈，否则视为无异议")
    ]
  }
};

/** 电量数据 = 本月读数 - 上月读数(海四达特有) */
function getOriginElecQuantity(lastMonth, currMonth) {
  if (isNaN(lastMonth) || isNaN(currMonth)) {
    return "/";
  }
  return (currMonth - lastMonth).toFixed(2) / 1;
}

/**
 * 判断该行的数值是否需要取负。
 * 合计行始终取负(保持原有汇总口径)。
 * 充放电方向由 cfg.chargeNegative 决定:
 *   - chargeNegative=true :充电取负、放电为正(以电表维度统计结算的正确口径)
 *   - chargeNegative=false:放电取负、充电为正(其余模板保持原有行为)
 * @param {object} row METER_ROWS 中的一行(用 direction 区分充/放/合计)
 * @param {object} cfg 模板配置
 */
function isNegativeRow(row, cfg) {
  if (row.direction === "合计") return false;
  return cfg.chargeNegative
    ? row.direction === "储能充电"
    : row.direction === "储能放电";
}

/**
 * 将单个 meter 转为通知单的多行数据。
 * 严格对齐原三个模板 processMeterData 的各自算法,差异由 cfg 控制。
 * @param {object} meter 单台电表原始数据
 * 
 * @param {object} cfg METER_TEMPLATES 中某模板配置
 * @returns {object[]} 行数组(长度 = METER_ROWS.length)
 */
export function buildMeterRows(meter, cfg) {
  const rate = cfg.fixedRate != null ? cfg.fixedRate : meter[cfg.magField];
  const pass = v => (cfg.useAbsCheck ? Math.abs(v) > 0 : v > 0);

  return METER_ROWS.map(row => {
    const neg = isNegativeRow(row, cfg);
    const lastReadingRaw = meter[row.lastReadingField];
    const currReadingRaw = meter[row.currReadingField];
    const billQtyRaw = meter[row.billQtyField];
    const priceRaw = meter[row.priceField];
    const feeRaw = meter[row.feeField];

    // 计费电量数值(含符号,不除倍率的场景直接取原值,否则除以倍率)
    const billQtyNum = billQtyRaw > 0
      ? (cfg.divideBillQty === false ? Number(billQtyRaw) : billQtyRaw / rate)
      : null;
    const billQtySigned = billQtyNum == null ? null : (neg ? -billQtyNum : billQtyNum);

    const data = {
      meterName: meter[row.meterNameField],
      direction: row.direction,
      itemLabel: row.itemLabel,
      rate,
      // 上月读数
      lastReading: lastReadingRaw / rate > 0 ? (cfg.divideDischarge ? lastReadingRaw / rate : lastReadingRaw).toFixed(2) : "/",
      // 本月读数
      currReading: currReadingRaw > 0 ? (cfg.divideActually ? currReadingRaw / rate : currReadingRaw).toFixed(2) : "/",
      // 计费电量(取负方向由 cfg.chargeNegative 决定,见 isNegativeRow;是否除倍率由 cfg.divideBillQty 决定)
      billQty: billQtySigned == null ? "/" : billQtySigned.toFixed(2),
      // 市电电价
      price: pass(priceRaw) ? priceRaw : "/",
      // 电费:common(divideBillQty=false)按"计费电量×电价"计算,不再单独除倍率;
      //       无电价的行(充/放电费、合计)回退到 feeRaw(取负方向由 cfg.chargeNegative 决定)。
      //       其余模板保持原口径(divideRatio2 控制是否除倍率)。
      fee: cfg.divideBillQty === false
        ? (billQtySigned != null && pass(priceRaw)
            ? (billQtySigned * priceRaw).toFixed(2)
            : (pass(feeRaw) ? (neg ? -feeRaw : feeRaw) : "/"))
        : (pass(feeRaw)
            ? neg
              ? cfg.divideRatio2 ? -(feeRaw / rate).toFixed(2) : -feeRaw
              : cfg.divideRatio2 ? (feeRaw / rate).toFixed(2) : feeRaw
            : "/")
    };

    if (cfg.showDevId) {
      data.devId = meter.devId;
    }
    if (cfg.showOriginElecQuantity) {
      data.elecData = getOriginElecQuantity(lastReadingRaw, currReadingRaw);
    }
    return data;
  });
}
